import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  onAuthStateChanged,
} from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { DEFAULT_XML } from '../constants';
import './LoginPage.css';

function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const ensureProjectsCollection = useCallback(async (firebaseUser) => {
    if (!firebaseUser) return;
    try {
      const defaultProjectRef = doc(db, 'users', firebaseUser.uid, 'projects', 'default-project');
      const existing = await getDoc(defaultProjectRef);
      if (existing.exists()) return;

      const ownerHandle = firebaseUser.displayName?.trim()
        || (firebaseUser.email ? firebaseUser.email.split('@')[0] : '')
        || 'creator';

      await setDoc(defaultProjectRef, {
        name: 'Default project',
        ownerUid: firebaseUser.uid,
        ownerHandle,
        ownerEmail: firebaseUser.email ?? '',
        xml: DEFAULT_XML,
        autoCreated: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Error ensuring projects subcollection', err);
    }
  }, []);

  const redirectAfterAuth = useCallback(
    (signedInUser) => {
      const profilePath = `/user?id=${encodeURIComponent(signedInUser.uid)}`;
      navigate(profilePath, { replace: true });
    },
    [navigate]
  );

  const ensureUserDocument = useCallback(async (firebaseUser) => {
    if (!firebaseUser) return;
    try {
      const userRef = doc(db, 'users', firebaseUser.uid);
      const snapshot = await getDoc(userRef);
      const payload = {
        email: firebaseUser.email ?? '',
        displayName: firebaseUser.displayName ?? '',
        lastLoginAt: serverTimestamp(),
      };
      if (!snapshot.exists()) {
        payload.createdAt = serverTimestamp();
      }

      await setDoc(userRef, payload, { merge: true });
      await ensureProjectsCollection(firebaseUser);
    } catch (err) {
      console.error('Error ensuring user document', err);
    }
  }, [ensureProjectsCollection]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (!currentUser) return;
      ensureUserDocument(currentUser).finally(() => {
        redirectAfterAuth(currentUser);
      });
    });

    return () => unsubscribe();
  }, [ensureUserDocument, redirectAfterAuth]);

  const handleEmailAuth = async (mode) => {
    setError('');

    if (!email || !password) {
      setError('Enter an email and password.');
      return;
    }

    setLoading(true);

    try {
      let credential;
      if (mode === 'signup') {
        credential = await createUserWithEmailAndPassword(auth, email, password);
      } else {
        credential = await signInWithEmailAndPassword(auth, email, password);
      }

      await ensureUserDocument(credential.user);
      redirectAfterAuth(credential.user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    setError('');
    setLoading(true);
    try {
      const credential = await signInWithPopup(auth, new GoogleAuthProvider());
      await ensureUserDocument(credential.user);
      redirectAfterAuth(credential.user);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="login-shell">
      <Link to="/" className="login-back">
        Back to home
      </Link>
      <div className="login-card">
        <h1>Welcome back</h1>
        <p>Sign in to access the Origami builder.</p>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            handleEmailAuth('login');
          }}
          className="login-form"
        >
          <label>
            Email
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              disabled={loading}
            />
          </label>
          <label>
            Password
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
              disabled={loading}
            />
          </label>

          {error && (
            <div className="login-error" role="alert">
              {error}
            </div>
          )}

          <button type="submit" disabled={loading} className="login-btn login-btn--primary">
            {loading ? 'Working…' : 'Log in'}
          </button>
        </form>

        <Link to="/signup" className="login-btn login-btn--ghost" style={{ textAlign: 'center', textDecoration: 'none', display: 'block' }}>
          Create an account
        </Link>

        <div className="login-divider">
          <span>or</span>
        </div>

        <button
          type="button"
          onClick={handleGoogleAuth}
          disabled={loading}
          className="login-btn login-btn--google"
        >
          Continue with Google
        </button>
      </div>
    </div>
  );
}

export default LoginPage;
